import { createFileRoute } from "@tanstack/react-router";
import { type ChangeEventHandler, useRef, useState } from "react";
import toast from "react-hot-toast";
import Download from "~icons/material-symbols/download";
import Upload from "~icons/material-symbols/upload";
import { Code, ConnectError } from "@connectrpc/connect";
import {
  CreateHokkaidoR8HouseholderPostalReturnRowError,
  CreateHokkaidoR8HouseholderPostalReturnRowError_Reason,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import { css } from "../../../../../styled-system/css";
import { LoadingOverlay } from "../../../../components/loading-overlay";
import { useCreateHokkaidoR8HouseholderPostalReturns } from "../../../../libs/admin/hooks";
import { downloadCsvFile } from "../../../../libs/utils/csv";
import { button } from "../../../../styles/buttonStyles";
import { contentWrapper } from "../../../../styles/contentWrapper";
import { input } from "../../../../styles/inputStyles";
import {
  pageTitle,
  sectionTitle,
  sectionWrapper,
  titleWrapper,
} from "../../../../styles/sectionStyles";
import { QR_CODE_COLUMN, type ParseResult, parseCsv } from "../paper-vouchers/-csv/parser";

export const Route = createFileRoute("/admin/hokkaido/postal-returns/")({
  component: RouteComponent,
});

// The serial numbers are placeholders; the cell format mirrors the postcard QR.
const SAMPLE_CSV_CONTENT = `${[
  QR_CODE_COLUMN,
  "https://p8n.jp/i/xxxxxxxxxxxxxxxxxxxxxx?u=%2Fhkp%2F234ABCDEFGHJ&t=h",
  "https://p8n.jp/i/xxxxxxxxxxxxxxxxxxxxxx?u=%2Fhkp%2F234KMNPQRSTV&t=h",
].join("\n")}\n`;

// https://pocketsign.slack.com/archives/C0AHLTUUD33/p1780892360732699?thread_ts=1780400615.732609&cid=C0AHLTUUD33
const PRESET_REASONS = [
  "転居不明",
  "宛所尋ね当たらず",
  "受取拒否",
  "宛名不完全",
];

const MAX_REASON_LENGTH = 200;

type RowDisplayError = {
  row: number;
  message: string;
};

type SubmitResult =
  | { kind: "idle" }
  | { kind: "success"; count: number }
  | { kind: "error"; errors: RowDisplayError[] }
  | { kind: "fatal"; message: string };

const formatReason = (
  reason: CreateHokkaidoR8HouseholderPostalReturnRowError_Reason,
): string => {
  switch (reason) {
    case CreateHokkaidoR8HouseholderPostalReturnRowError_Reason.MALFORMED_CODE:
      return "シリアルナンバーの形式が正しくありません";
    case CreateHokkaidoR8HouseholderPostalReturnRowError_Reason.DUPLICATE_IN_REQUEST:
      return "CSV 内で重複しているシリアルナンバーです";
    case CreateHokkaidoR8HouseholderPostalReturnRowError_Reason.CODE_NOT_FOUND:
      return "登録されていないシリアルナンバーです";
    default:
      return "不明なエラー";
  }
};

const fatalMessageFromConnect = (error: unknown): string => {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied) {
      return "権限がありません。組織を確認してください。";
    }
    return error.message;
  }
  return error instanceof Error
    ? error.message
    : "通信に失敗しました。時間を置いて再度お試しください。";
};

function RouteComponent() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState("");
  const [reason, setReason] = useState("");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitResult>({ kind: "idle" });

  const mutation = useCreateHokkaidoR8HouseholderPostalReturns();

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const result = await parseCsv(text);
      setParseResult(result);
      setFileName(file.name);
      setSubmitResult({ kind: "idle" });
      if (result.errors.length === 0 && result.rows.length === 0) {
        toast("データ行がありません", { icon: "ℹ️" });
      }
    } catch (err) {
      console.error("CSV parsing failed", err);
      setParseResult(null);
      setSubmitResult({ kind: "idle" });
      toast.error(
        err instanceof Error ? err.message : "CSVの読み込みに失敗しました",
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadSample = () => {
    downloadCsvFile("postal_return_sample.csv", SAMPLE_CSV_CONTENT);
  };

  const trimmedReason = reason.trim();

  const handleRegister = async () => {
    if (
      !parseResult ||
      parseResult.errors.length > 0 ||
      parseResult.rows.length === 0 ||
      trimmedReason === ""
    ) {
      return;
    }
    setSubmitResult({ kind: "idle" });
    try {
      await mutation.mutateAsync({
        codes: parseResult.rows.map((r) => r.code),
        reason: trimmedReason,
      });
      setSubmitResult({ kind: "success", count: parseResult.rows.length });
    } catch (error) {
      console.error("Failed to register postal returns:", error);
      if (
        error instanceof ConnectError &&
        error.code === Code.FailedPrecondition
      ) {
        const details = error.findDetails(
          CreateHokkaidoR8HouseholderPostalReturnRowError,
        );
        if (details.length > 0) {
          const rowErrors: RowDisplayError[] = details.map((d) => ({
            row: parseResult.rows[d.index]?.row ?? d.index + 1,
            message: formatReason(d.reason),
          }));
          rowErrors.sort((a, b) => a.row - b.row);
          setSubmitResult({ kind: "error", errors: rowErrors });
          return;
        }
      }
      setSubmitResult({ kind: "fatal", message: fatalMessageFromConnect(error) });
    }
  };

  const parseErrorsAsDisplay: RowDisplayError[] =
    parseResult?.errors.map((e) => ({ row: e.row, message: e.message })) ?? [];
  const parseRowCount = parseResult?.rows.length ?? 0;
  const canRegister =
    !!parseResult &&
    parseResult.errors.length === 0 &&
    parseRowCount > 0 &&
    trimmedReason !== "" &&
    !mutation.isPending &&
    submitResult.kind !== "success";

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>ハガキ返戻登録</h1>
      </div>
      <div className={css({ display: "flex", flexDirection: "column", gap: "24px" })}>
        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>返戻理由</div>
          <div className={css({ display: "flex", flexDirection: "column", gap: "12px" })}>
            <p className={css({ fontSize: "14px", color: "text.secondary" })}>
              配送業者から差し戻された理由を入力してください。アップロードした CSV
              の全シリアルナンバーに同じ理由が記録されます。よく使う理由はボタンで入力できます。
            </p>
            <div className={css({ display: "flex", gap: "8px", flexWrap: "wrap" })}>
              {PRESET_REASONS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={button({ color: "secondary" })}
                  onClick={() => setReason(preset)}
                  disabled={mutation.isPending}
                >
                  {preset}
                </button>
              ))}
            </div>
            <input
              type="text"
              className={input()}
              placeholder="返戻理由を入力"
              value={reason}
              maxLength={MAX_REASON_LENGTH}
              onChange={(e) => setReason(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
        </div>

        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>CSVファイルを選択</div>
          <div className={css({ display: "flex", flexDirection: "column", gap: "12px" })}>
            <p className={css({ fontSize: "14px", color: "text.secondary" })}>
              ヘッダー付き CSV を読み込みます。<code>{QR_CODE_COLUMN}</code>{" "}
              カラムに <code>https://p8n.jp/i/...?u=%2Fhkp%2F&lt;シリアルナンバー&gt;&amp;t=h</code>{" "}
              形式の URL を入れてください。その他のカラムは無視されます。1 行でも不備があると CSV
              全体が登録対象外となり、どのシリアルナンバーも返戻済みになりません。
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={mutation.isPending}
            />
            <div className={css({ display: "flex", gap: "12px", flexWrap: "wrap" })}>
              <button
                type="button"
                className={button({ color: "secondary" })}
                onClick={handleDownloadSample}
              >
                <Download width="20" height="20" />
                サンプルCSVをダウンロード
              </button>
              <button
                type="button"
                className={button({ color: "secondary" })}
                onClick={() => fileInputRef.current?.click()}
                disabled={mutation.isPending}
              >
                <Upload width="20" height="20" />
                CSVファイルを読み込む
              </button>
            </div>
            {fileName && (
              <p className={css({ fontSize: "14px", color: "text.secondary" })}>
                読み込み済み: {fileName}（{parseRowCount} 件）
              </p>
            )}
          </div>
        </div>

        {parseErrorsAsDisplay.length > 0 && (
          <ErrorList title="CSV 解析時のエラー" errors={parseErrorsAsDisplay} />
        )}

        {submitResult.kind === "error" && (
          <ErrorList title="登録時のエラー" errors={submitResult.errors} />
        )}

        {submitResult.kind === "fatal" && (
          <div className={css({ color: "text.accentError", fontSize: "14px" })}>
            {submitResult.message}
          </div>
        )}

        {submitResult.kind === "success" && (
          <div className={css({ fontSize: "14px", color: "text.secondary" })}>
            {submitResult.count} 件のシリアルナンバーを返戻済みとして登録しました。
          </div>
        )}

        <div className={css({ display: "flex", gap: "12px", alignItems: "center" })}>
          <button
            type="button"
            className={button({ color: "primary" })}
            onClick={handleRegister}
            disabled={!canRegister}
          >
            返戻登録 {parseRowCount > 0 ? `(${parseRowCount}件)` : ""}
            {mutation.isPending && <LoadingOverlay size="sm" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorList({ title, errors }: { title: string; errors: RowDisplayError[] }) {
  return (
    <div
      className={css({
        color: "text.accentError",
        fontSize: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      })}
    >
      <span>
        {errors.length}件のエラーがあります（{title}）
      </span>
      <ul className={css({ listStyle: "disc", pl: "2em" })}>
        {errors.map((e, i) => (
          <li key={`${e.row}-${i}`}>
            {e.row}行目: {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
